import type { GatewayBrowserClient } from "../gateway.ts";
import type { ConfigState } from "./config.ts";
import { normalizeAgentId } from "../../../../src/routing/session-key.js";
import { loadAgentFleetGraph } from "./agent-fleet-graph.ts";
import {
  findAgentListIndex,
  getAgentAllowAgents,
  getAgentsConfigList,
  isValidAgentId,
  type AgentListEntryRecord,
} from "./agents-config.ts";
import {
  patchAgentIdentityFields,
  patchAgentModel,
  patchAgentSkills,
  patchAgentToolsProfile,
  resolveAgentModelPrimary,
  resolveAgentSkillsAllowlist,
  resolveAgentToolsProfile,
  resolveEffectiveModelLabel,
} from "./agents-list-config-patch.ts";
import { loadAgents } from "./agents.ts";
import { serializeConfigForm } from "./config/form-utils.ts";

export type FleetGraphBindingDraft = {
  bindingId: string;
  agentId: string;
  keywords: string;
  priority: number;
  fallback: boolean;
};

export type FleetGraphConfigHost = ConfigState & {
  client: GatewayBrowserClient | null;
  connected: boolean;
  fleetGraphEditorSaving: boolean;
  fleetGraphEditorError: string | null;
};

function cloneConfig(config?: Record<string, unknown>): Record<string, unknown> | null {
  if (!config) {
    return null;
  }
  return JSON.parse(JSON.stringify(config)) as Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function ensureAgentsList(config: Record<string, unknown>): AgentListEntryRecord[] {
  const agents = asRecord(config.agents);
  if (!config.agents || typeof config.agents !== "object") {
    config.agents = agents;
  }
  if (!Array.isArray(agents.list)) {
    agents.list = [];
  }
  return agents.list as AgentListEntryRecord[];
}

function asBindingArray(config: Record<string, unknown>): Record<string, unknown>[] {
  const workforce = asRecord(config.workforce);
  const bindings = workforce.bindings;
  if (!Array.isArray(bindings)) {
    return [];
  }
  return bindings.map((entry) => asRecord(entry));
}

function ensureWorkforce(config: Record<string, unknown>): Record<string, unknown> {
  const workforce = asRecord(config.workforce);
  workforce.enabled = workforce.enabled !== false;
  config.workforce = workforce;
  return workforce;
}

export function listFleetGraphBindings(
  config: Record<string, unknown> | null,
): FleetGraphBindingDraft[] {
  if (!config) {
    return [];
  }
  return asBindingArray(config).map((row) => {
    const match = asRecord(row.match);
    const keywords = Array.isArray(match.keywords)
      ? match.keywords.map((k) => String(k)).join(", ")
      : "";
    return {
      bindingId: String(row.bindingId ?? ""),
      agentId: String(row.agentId ?? ""),
      keywords,
      priority: typeof row.priority === "number" ? row.priority : 50,
      fallback: row.fallback === true,
    };
  });
}

export function listFleetGraphAgentIds(config: Record<string, unknown> | null): string[] {
  return getAgentsConfigList(config)
    .map((entry) => (entry.id ? normalizeAgentId(entry.id) : ""))
    .filter(Boolean);
}

export async function fetchFleetGraphConfig(
  host: FleetGraphConfigHost,
): Promise<Record<string, unknown>> {
  if (!host.client || !host.connected) {
    throw new Error("Not connected to gateway.");
  }
  const snapshot = await host.client.request<{
    config?: Record<string, unknown>;
  }>("config.get", {});
  const config = cloneConfig(snapshot.config);
  if (!config) {
    throw new Error("Config unavailable.");
  }
  return config;
}

export async function mutateFleetGraphConfig(
  host: FleetGraphConfigHost & {
    refreshAgentFleetGraph?: () => Promise<void>;
  },
  mutate: (config: Record<string, unknown>) => void,
  opts?: { allowInstanceDefaultChange?: boolean },
): Promise<void> {
  if (!host.client || !host.connected || host.fleetGraphEditorSaving) {
    return;
  }
  host.fleetGraphEditorSaving = true;
  host.fleetGraphEditorError = null;
  try {
    const snapshot = await host.client.request<{
      hash?: string;
      config?: Record<string, unknown>;
    }>("config.get", {});
    const baseHash = snapshot.hash;
    const currentConfig = cloneConfig(snapshot.config);
    if (!baseHash || !currentConfig) {
      throw new Error("Config hash missing; reload and retry.");
    }
    mutate(currentConfig);
    const raw = serializeConfigForm(currentConfig);
    await host.client.request("config.set", {
      raw,
      baseHash,
      ...(opts?.allowInstanceDefaultChange ? { allowInstanceDefaultChange: true } : {}),
    });
    await loadAgents(host as unknown as Parameters<typeof loadAgents>[0], { quiet: true });
    await loadAgentFleetGraph(host as unknown as Parameters<typeof loadAgentFleetGraph>[0]);
  } catch (err) {
    host.fleetGraphEditorError = String(err);
    throw err;
  } finally {
    host.fleetGraphEditorSaving = false;
  }
}

function scrubAgentReferences(config: Record<string, unknown>, agentId: string): void {
  const id = normalizeAgentId(agentId);
  for (const entry of getAgentsConfigList(config)) {
    const entryId = entry.id ? normalizeAgentId(entry.id) : "";
    if (!entryId) {
      continue;
    }
    const allow = entry.subagents?.allowAgents;
    if (!Array.isArray(allow)) {
      continue;
    }
    const next = allow.filter((target) => normalizeAgentId(target) !== id);
    if (next.length !== allow.length) {
      if (next.length > 0) {
        entry.subagents = { ...entry.subagents, allowAgents: next };
      } else if (entry.subagents) {
        delete entry.subagents.allowAgents;
        if (Object.keys(entry.subagents).length === 0) {
          delete entry.subagents;
        }
      }
    }
  }
  const workforce = ensureWorkforce(config);
  workforce.bindings = asBindingArray(config).filter(
    (row) => normalizeAgentId(String(row.agentId ?? "")) !== id,
  );
}

export type FleetGraphConfigDraft = {
  modelPrimary: string | null;
  toolsProfile: string | null;
  skillsAllowlist: string[] | null;
  identityName: string;
  identityEmoji: string;
  identityTheme: string;
};

export function buildFleetGraphConfigDraft(
  config: Record<string, unknown>,
  agentId: string,
): FleetGraphConfigDraft {
  const id = normalizeAgentId(agentId);
  const entry = getAgentsConfigList(config).find((row) => normalizeAgentId(row.id ?? "") === id);
  return {
    modelPrimary: resolveAgentModelPrimary(config, id),
    toolsProfile: resolveAgentToolsProfile(config, id),
    skillsAllowlist: resolveAgentSkillsAllowlist(config, id),
    identityName: entry?.identity?.name ?? "",
    identityEmoji: entry?.identity?.emoji ?? "",
    identityTheme: entry?.identity?.theme ?? "",
  };
}

export function resolveFleetEffectiveModelLabel(
  config: Record<string, unknown> | null,
  agentId: string,
): string {
  return resolveEffectiveModelLabel(config, agentId);
}

export async function saveFleetGraphAgent(
  host: FleetGraphConfigHost & { refreshAgentFleetGraph?: () => Promise<void> },
  params: {
    mode: "create" | "edit";
    id: string;
    name: string;
    workspace: string;
    setDefault: boolean;
    allowAll: boolean;
    allowAgents: string[];
    config?: FleetGraphConfigDraft;
  },
): Promise<void> {
  const rawId = params.id.trim();
  if (!isValidAgentId(rawId)) {
    throw new Error("Agent id must be 1–64 characters: letters, numbers, underscore, hyphen.");
  }
  const id = normalizeAgentId(rawId);
  const allowAgents = params.allowAll ? ["*"] : params.allowAgents.map((a) => normalizeAgentId(a));

  await mutateFleetGraphConfig(
    host,
    (config) => {
      const list = ensureAgentsList(config);
      if (params.mode === "create") {
        if (list.some((entry) => normalizeAgentId(entry.id ?? "") === id)) {
          throw new Error(`Agent "${id}" already exists.`);
        }
        const entry: AgentListEntryRecord = { id };
        const name = params.name.trim();
        if (name) {
          entry.name = name;
        }
        const workspace = params.workspace.trim();
        if (workspace) {
          entry.workspace = workspace;
        }
        if (allowAgents.length > 0) {
          entry.subagents = { allowAgents };
        }
        list.push(entry);
      } else {
        const index = findAgentListIndex(config, id);
        if (index < 0) {
          throw new Error(`Agent "${id}" not found.`);
        }
        const entry = list[index];
        if (!entry) {
          throw new Error(`Agent "${id}" not found.`);
        }
        const name = params.name.trim();
        if (name) {
          entry.name = name;
        } else {
          delete entry.name;
        }
        const workspace = params.workspace.trim();
        if (workspace) {
          entry.workspace = workspace;
        } else {
          delete entry.workspace;
        }
        if (allowAgents.length > 0) {
          entry.subagents = { ...entry.subagents, allowAgents };
        } else if (entry.subagents) {
          delete entry.subagents.allowAgents;
          if (Object.keys(entry.subagents).length === 0) {
            delete entry.subagents;
          }
        }
      }

      if (params.setDefault) {
        for (const row of list) {
          if (row && typeof row === "object") {
            delete row.default;
          }
        }
        const index = findAgentListIndex(config, id);
        if (index >= 0 && list[index]) {
          list[index].default = true;
        }
      }

      if (params.config) {
        patchAgentModel(config, id, params.config.modelPrimary);
        patchAgentToolsProfile(config, id, params.config.toolsProfile);
        patchAgentSkills(config, id, params.config.skillsAllowlist);
        patchAgentIdentityFields(config, id, {
          identityName: params.config.identityName,
          identityEmoji: params.config.identityEmoji,
          identityTheme: params.config.identityTheme,
        });
      }
    },
    params.setDefault ? { allowInstanceDefaultChange: true } : undefined,
  );
}

export async function saveFleetGraphBehaviorFiles(
  host: FleetGraphConfigHost & { client: GatewayBrowserClient | null; connected: boolean },
  agentId: string,
  files: { soul?: string; agents?: string },
): Promise<void> {
  if (!host.client || !host.connected) {
    throw new Error("Not connected to gateway.");
  }
  const id = normalizeAgentId(agentId);
  if (files.soul !== undefined) {
    await host.client.request("agents.files.set", {
      agentId: id,
      name: "SOUL.md",
      content: files.soul,
    });
  }
  if (files.agents !== undefined) {
    await host.client.request("agents.files.set", {
      agentId: id,
      name: "AGENTS.md",
      content: files.agents,
    });
  }
}

export async function deleteFleetGraphAgent(
  host: FleetGraphConfigHost & { refreshAgentFleetGraph?: () => Promise<void> },
  agentId: string,
): Promise<void> {
  const id = normalizeAgentId(agentId);
  await mutateFleetGraphConfig(host, (config) => {
    const list = ensureAgentsList(config);
    const index = findAgentListIndex(config, id);
    if (index < 0) {
      throw new Error(`Agent "${id}" is not in agents.list.`);
    }
    if (list.length <= 1) {
      throw new Error("Cannot remove the only agent in agents.list.");
    }
    const entry = list[index];
    if (entry?.default) {
      throw new Error("Unset instance default before deleting this agent.");
    }
    scrubAgentReferences(config, id);
    list.splice(index, 1);
  });
}

export async function saveFleetGraphBindings(
  host: FleetGraphConfigHost & { refreshAgentFleetGraph?: () => Promise<void> },
  bindings: FleetGraphBindingDraft[],
): Promise<void> {
  const seen = new Set<string>();
  for (const row of bindings) {
    const bindingId = row.bindingId.trim();
    if (!bindingId) {
      throw new Error("Each route needs a binding id.");
    }
    if (seen.has(bindingId)) {
      throw new Error(`Duplicate binding id: ${bindingId}`);
    }
    seen.add(bindingId);
    if (!row.agentId.trim()) {
      throw new Error(`Binding "${bindingId}" needs a target agent.`);
    }
  }

  await mutateFleetGraphConfig(host, (config) => {
    const workforce = ensureWorkforce(config);
    workforce.bindings = bindings.map((row) => {
      const keywords = row.keywords
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
      return {
        bindingId: row.bindingId.trim(),
        agentId: normalizeAgentId(row.agentId),
        match: keywords.length > 0 ? { keywords } : {},
        priority: row.priority,
        fallback: row.fallback,
      };
    });
  });
}

export function buildAgentEditorDraft(
  config: Record<string, unknown>,
  agentId: string,
  allAgentIds: string[],
): {
  id: string;
  name: string;
  workspace: string;
  setDefault: boolean;
  allowAll: boolean;
  allowAgents: string[];
} {
  const id = normalizeAgentId(agentId);
  const list = getAgentsConfigList(config);
  const entry = list.find((row) => normalizeAgentId(row.id ?? "") === id);
  const allow = getAgentAllowAgents(config, id) ?? [];
  const allowAll = allow.includes("*");
  return {
    id,
    name: entry?.name ?? entry?.identity?.name ?? "",
    workspace: entry?.workspace ?? "",
    setDefault: entry?.default === true,
    allowAll,
    allowAgents: allowAll
      ? allAgentIds.filter((other) => other !== id)
      : allow.filter((target) => target !== "*"),
  };
}

export function newFleetBindingDraft(agentId = ""): FleetGraphBindingDraft {
  const suffix = Date.now().toString(36);
  return {
    bindingId: agentId ? `route-${normalizeAgentId(agentId)}-${suffix}` : `route-${suffix}`,
    agentId,
    keywords: "",
    priority: 50,
    fallback: false,
  };
}
